import React, {
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
// @ts-ignore
import styles from './GridFilter.module.scss';
import LocalStorage from '../../../../Services/LocalStorage';
import { useAppContext } from '../../../../Services/AppContext';
import { useEffectAsync } from '../../../../helpers';
import {
	useLazyGetGridFilterQuery,
	useSaveGridFilterMutation,
} from '../../../../graphql/graphql';
import useTranslation from '../../../../Hooks/useTranslation';
import { isEqual, cloneDeep } from 'lodash';
import { joinClasses } from '../../../../Utils/className.util';

interface GridFilterProps {
	children: React.ReactElement | React.ReactElement[];
	onChange: (values: object) => void;
	storageKey?: string;
	storageType?: 'api' | 'local';
	isFilterOpen?: boolean;
	dataCount?: number | null;
	confirmButton?: boolean;
	defaultValues?: object;
	customButtons?: React.ReactElement;
	formClassName?: string;
}

const GridFilter: React.FC<GridFilterProps> = ({
	children,
	onChange,
	storageKey,
	storageType = 'api',
	isFilterOpen = false,
	dataCount,
	confirmButton = false,
	defaultValues,
	customButtons,
	formClassName,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [hasMoreFilters, setHasMoreFilters] = useState(true);
	const [items, setItems] = useState<React.ReactChild[]>([]);
	const ref = useRef<any>();
	const formMethods = useForm({ defaultValues: defaultValues || {} });
	const { userData, workplaceId } = useAppContext();
	const [getStoredFilter] = useLazyGetGridFilterQuery();
	const gridFilterKey = `${storageKey}_${workplaceId}`;
	const [saveGridFilter, { isLoading: isSaving }] = useSaveGridFilterMutation();
	const { t } = useTranslation();
	const [lastDefValues, setLastDefValues] = useState<object>();

	const values = formMethods.watch();
	const memoizedValues = useMemo(() => JSON.stringify(values), [values]);

	const hasFilter = useMemo(() => {
		const filters = Object.values(values).filter(val => !!val);
		return filters.length > 0;
	}, [values]);

	const [isLoading, setIsLoading] = useState(true);

	const loadDataFromStorage = (key: string) => {
		const data = LocalStorage.get(key);
		return data || {};
	};

	const saveToLocalStorage = (key: string, data: any) => {
		if (data) {
			LocalStorage.set(key, data);
		}
	};

	useEffect(() => {
		if (!isEqual(defaultValues, lastDefValues)) {
			setLastDefValues(cloneDeep(defaultValues));
			formMethods.reset(defaultValues);
		}
	}, [defaultValues]);

	useLayoutEffect(() => {
		handleCalculateFilterDimensions();
	}, [isOpen, children]);

	useEffect(() => {
		// @ts-ignore
		window.addEventListener('resize', handleCalculateFilterDimensions);
		return () => {
			// @ts-ignore
			window.removeEventListener('resize', handleCalculateFilterDimensions);
		};
	}, []);

	useEffectAsync(async () => {
		if (storageKey) {
			const data =
				storageType === 'api'
					? (await getStoredFilter({ key: gridFilterKey }).unwrap()).userFilter
							?.value
					: loadDataFromStorage(gridFilterKey);

			if (data) {
				formMethods.reset(data);
				formMethods.handleSubmit(() => onChange(data))();
			}
		}
	}, []);

	useEffect(() => {
		setIsOpen(isFilterOpen);
	}, [isFilterOpen]);

	const handleCalculateFilterDimensions = () => {
		if (ref.current) {
			const wrapperWidth = ref.current.clientWidth;
			const tempItems: React.ReactChild[] = [];
			let a = 0;

			React.Children.map(children, child => {
				if (child.props?.visible === false) {
					return;
				}
				a = a + child.props.width + 6;
				if (isOpen || a < wrapperWidth) {
					tempItems.push(child);
				}
			});
			setHasMoreFilters(a > wrapperWidth);
			setItems(tempItems);
		}
	};

	const handleToggleMore = () => {
		setIsOpen(prev => !prev);
	};

	const handleOnChange = ({ enableAutocomplete }: any) => {
		if (enableAutocomplete) {
			if (!formMethods) {
				return;
			}
			formMethods.handleSubmit(onChange)();
		}
	};

	const handleResetFilter = () => {
		// https://github.com/orgs/react-hook-form/discussions/7589?sort=new#discussioncomment-6500750
		const defaultValues: Record<string, null> = formMethods.getValues();
		const resetValues = Object.keys(defaultValues).reduce((acc, key) => {
			// @ts-ignore
			acc[key] = null;
			return acc;
		}, {});
		formMethods.reset(resetValues);
		formMethods.handleSubmit(() => onChange({}))();
	};

	useEffectAsync(async () => {
		if (!isLoading) {
			if (storageKey && values) {
				if (storageType === 'api' && !isSaving) {
					await saveGridFilter({
						input: {
							key: gridFilterKey,
							data: values,
						},
					});
				} else {
					saveToLocalStorage(gridFilterKey, values);
				}
			}
		} else {
			setIsLoading(false);
		}
	}, [memoizedValues]);

	return (
		<>
			<div className={styles.wrapper}>
				<FormWrapper
					style={{ flex: 1 }}
					formMethods={formMethods}
					className={formClassName}>
					<div className={styles.form} ref={ref}>
						{items.map((item: any, index: number) => {
							return (
								<React.Fragment key={index}>
									{React.cloneElement(item, {
										...item.props,
										customOnChange: handleOnChange,
									})}
								</React.Fragment>
							);
						})}
					</div>
				</FormWrapper>
				<div className={styles.controls}>
					{confirmButton && (
						<LinkButton
							title='base.shared.confirm'
							theme={Theme.BLUE}
							onClick={() => handleOnChange({ enableAutocomplete: true })}
						/>
					)}
					{hasMoreFilters && (
						<div className={styles.toggleButton}>
							<LinkButton
								title='base.additionalFilters'
								theme={Theme.BLUE}
								onClick={handleToggleMore}
							/>
							{isOpen ? (
								<img
									src='/dist/icons/chevron-down-backgrounded-open.svg'
									alt='arrow up'
								/>
							) : (
								<img
									src='/dist/icons/chevron-down-backgrounded.svg'
									alt='arrow down'
								/>
							)}
						</div>
					)}
					{hasFilter && (
						<LinkButton
							title='ublaboo_datagrid.reset_filter'
							theme={Theme.BLUE}
							onClick={handleResetFilter}
						/>
					)}
				</div>
				{customButtons}
			</div>
			{!!dataCount && (
				<div className={styles.filterInfo}>
					<div className={styles.count}>
						{t('base.numberOfRecords')}: {dataCount}
					</div>
				</div>
			)}
		</>
	);
};

export default GridFilter;
